import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Search, ExternalLink, ArrowRight } from "lucide-react";
import type { Company } from "@shared/schema";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function CompanyCard({ company }: { company: Company }) {
  return (
    <Link
      href={`/companies/${company.slug}`}
      data-testid={`card-featured-company-${company.id}`}
      className="group flex items-start gap-4 rounded-xl border bg-card p-5 hover:shadow-md transition-all hover:border-primary/40 cursor-pointer"
    >
      <Avatar className="h-14 w-14 rounded-lg shrink-0">
        {company.logo ? (
          <AvatarImage src={company.logo} alt={company.name} className="object-contain" />
        ) : null}
        <AvatarFallback className="rounded-lg text-base font-semibold bg-primary/10 text-primary">
          {initials(company.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors">
          {company.name}
        </h3>
        {company.industry && (
          <p className="text-xs text-muted-foreground mt-0.5">{company.industry}</p>
        )}
        {company.headquarters && (
          <p className="text-xs text-muted-foreground mt-0.5">{company.headquarters}</p>
        )}
        {company.description && (
          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{company.description}</p>
        )}
        <div className="flex items-center gap-1 text-xs font-medium text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
          View profile <ArrowRight className="h-3 w-3" />
        </div>
      </div>
      {company.website && (
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 mt-0.5 group-hover:text-muted-foreground transition-colors" />
      )}
    </Link>
  );
}

function CompanyCardSkeleton() {
  return (
    <div className="flex items-start gap-4 rounded-xl border bg-card p-5">
      <Skeleton className="h-14 w-14 rounded-lg shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-3 w-full" />
      </div>
    </div>
  );
}

export default function FeaturedCompaniesPage() {
  const [search, setSearch] = useState("");

  const { data: companies, isLoading } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
  });

  const filtered = (companies ?? []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.industry ?? "").toLowerCase().includes(q) ||
      (c.headquarters ?? "").toLowerCase().includes(q) ||
      (c.description ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <>
      <Helmet>
        <title>Featured Companies | Future Manufacturing</title>
        <meta
          name="description"
          content="Curated profiles of the companies shaping the future of manufacturing — from global distributors to specialist component makers."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Companies" }]} />
        </div>

        <main className="flex-1">
          {/* Hero */}
          <section className="border-b bg-muted/30 py-14">
            <div className="mx-auto max-w-7xl px-4">
              <Badge variant="secondary" className="mb-4">Featured Companies</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                Companies shaping manufacturing
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mb-8">
                A curated showcase of the distributors, manufacturers, and specialists driving the future of the industry.
              </p>
              {/* Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search companies…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="input-company-search"
                />
              </div>
            </div>
          </section>

          {/* Grid */}
          <section className="py-14">
            <div className="mx-auto max-w-7xl px-4">
              {isLoading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <CompanyCardSkeleton key={i} />
                  ))}
                </div>
              ) : filtered.length > 0 ? (
                <>
                  <p className="text-sm text-muted-foreground mb-6">
                    {filtered.length} compan{filtered.length !== 1 ? "ies" : "y"}
                    {search.trim() ? ` matching "${search}"` : ""}
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((company) => (
                      <CompanyCard key={company.id} company={company} />
                    ))}
                  </div>
                </>
              ) : (companies ?? []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">No companies yet</h2>
                  <p className="text-muted-foreground max-w-sm">
                    Company profiles will appear here once they are added.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <Search className="h-10 w-10 text-muted-foreground mb-4" />
                  <h2 className="text-xl font-semibold mb-2">No results found</h2>
                  <p className="text-muted-foreground">
                    Try a different search term.
                  </p>
                </div>
              )}
            </div>
          </section>

        </main>

        <SiteFooter />
      </div>
    </>
  );
}
